// @flow
import { type ActionType, createAction, handleActions } from 'redux-actions';
import produce from 'immer';
import { applyPenders } from 'redux-pender';
import * as EventsAPI from 'lib/api/events';

const GET_RECENT_EVENTS = 'eventing/GET_RECENT_EVENTS';
const PREFETCH_RECENT_EVENTS = 'eventing/PREFETCH';
const REVEAL_PREFETCHED = 'eventing/REVEAL_PREFETCHED';
const CLEAR_USER_EVENTS = 'eventing/CLEAR_USER_EVENTS';
const GET_USER_EVENTS = 'eventing/GET_USER_EVENTS';
const PREFETCH_USER_EVENTS = 'eventing/PREFETCH_USER_EVENTS';
const GET_TRENDING_EVENTS = 'eventing/GET_TRENDING_EVENTS';
const PREFETCH_TRENDING_EVENTS = 'eventing/PREFETCH_TRENDING_EVENTS';
const GET_TAG_EVENTS = 'eventing/GET_TAG_EVENTS';
const PREFETCH_TAG_EVENTS = 'eventing/PREFETCH_TAG_EVENTS';
const GET_CATEGORY_EVENTS = 'eventing/GET_CATEGORY_EVENTS';
const PREFETCH_CATEGORY_EVENTS = 'eventing/PREFETCH_CATEGORY_EVENTS';
const CLEAR_CATEGORY_EVENTS = 'eventing/CLEAR_CATEGORY_EVENTS';
const CLEAR_TAG_EVENTS = 'eventing/CLEAR_TAG_EVENTS';
const GET_TEMP_EVENTS = 'eventing/GET_TEMP_EVENTS';
const PREFETCH_TEMP_EVENTS = 'eventing/PREFETCH_TEMP_EVENTS';
const REMOVE_TEMP_EVENT = 'eventing/REMOVE_TEMP_EVENT';
const FLUSH_LIST = 'eventing/FLUSH_LIST';

// potential improvement :: remove duplicates
export const actionCreators = {
  getRecentEvents: createAction(GET_RECENT_EVENTS, EventsAPI.getPublicEvents),
  prefetchRecentEvents: createAction(PREFETCH_RECENT_EVENTS, EventsAPI.getPublicEvents),
  revealPrefetched: createAction(REVEAL_PREFETCHED, (type: string) => type),
  clearUserEvents: createAction(CLEAR_USER_EVENTS),
  getUserEvents: createAction(
    GET_USER_EVENTS,
    EventsAPI.getUserEvents,
    (meta: { username: string, tag?: string }) => meta,
  ),
  prefetchUserEvents: createAction(PREFETCH_USER_EVENTS, EventsAPI.getUserEvents),
  getTrendingEvents: createAction(GET_TRENDING_EVENTS, EventsAPI.getTrendingEvents),
  prefetchTrendingEvents: createAction(PREFETCH_TRENDING_EVENTS, EventsAPI.getTrendingEvents),
  getTagEvents: createAction(
    GET_TAG_EVENTS,
    EventsAPI.getPublicEventsByTag,
    (meta: { tag: string }) => meta,
  ),
  prefetchTagEvents: createAction(PREFETCH_TAG_EVENTS, EventsAPI.getPublicEventsByTag),
  getCategoryEvents: createAction(
    GET_CATEGORY_EVENTS,
    EventsAPI.getPublicEventsByCategory,
    (meta: { category: string, sort?: string }) => meta,
  ),
  prefetchCategoryEvents: createAction(
    PREFETCH_CATEGORY_EVENTS,
    EventsAPI.getPublicEventsByCategory,
  ),
  clearCategoryEvents: createAction(CLEAR_CATEGORY_EVENTS),
  clearTagEvents: createAction(CLEAR_TAG_EVENTS),
  getTempEvents: createAction(GET_TEMP_EVENTS, EventsAPI.getTempEvents),
  prefetchTempEvents: createAction(PREFETCH_TEMP_EVENTS, EventsAPI.getTempEvents),
  removeTempEvent: createAction(REMOVE_TEMP_EVENT, (eventId: string) => eventId),
  flushList: createAction(FLUSH_LIST),
};

export type EventItem = {
  id: string,
  title: string,
  body: string,
  thumbnail: string,
  is_markdown: boolean,
  is_private: boolean,
  released_at: string,
  created_at: string,
  updated_at: string,
  tags: string[],
  categories: any[],
  url_slug: string,
  likes: number,
  comments_count: number,
  user: {
    id: string,
    username: string,
    eventing_name: string,
    short_bio: string,
    thumbnail: ?string,
  },
  meta: any,
};

export type EventingSet = {
  events: ?(EventItem[]),
  prefetched: ?(EventItem[]),
  end: boolean,
};

export type Eventing = {
  trendingMap: {
    [id: string]: boolean,
  },
  trending: EventingSet,
  recent: EventingSet,
  user: EventingSet & { currentUsername: ?string, currentTag: ?string },
  tag: EventingSet & { currentTag: ?string },
  category: EventingSet & { currentCategory: ?string },
  temp: EventingSet,
};

type RevealPrefetchedAction = ActionType<typeof actionCreators.revealPrefetched>;
type EventsResponseAction = {
  type: string,
  payload: {
    data: EventItem[],
    category?: ?string,
    sort?: ?string,
  },
  meta: any,
};
type RemoveTempEventAction = ActionType<typeof actionCreators.removeTempEvent>;

export interface EventingActionCreators {
  getRecentEvents(cursor: string, category: string, sort: string): any;

  prefetchRecentEvents(cursor: string, category: string, sort: string): any;

  revealPrefetched(type: string): any;

  getUserEvents(payload: EventsAPI.GetUserEventsPayload): any;

  prefetchUserEvents(payload: EventsAPI.GetUserEventsPayload): any;

  getTrendingEvents(): any;

  prefetchTrendingEvents(offset: number): any;

  getTagEvents(payload: EventsAPI.GetPublicEventsByTagPayload): any;

  prefetchTagEvents(payload: EventsAPI.GetPublicEventsByTagPayload): any;

  getCategoryEvents(payload: EventsAPI.GetPublicEventsByCategoryPayload): any;

  prefetchCategoryEvents(payload: EventsAPI.GetPublicEventsByCategoryPayload): any;

  getTempEvents(payload: EventsAPI.GetTempEventsPayload): any;

  prefetchTempEvents(payload: EventsAPI.GetTempEventsPayload): any;

  clearCategoryEvents(): any;

  clearTagEvents(): any;

  clearUserEvents(): any;

  removeTempEvent(eventId: string): any;

  flushList(): any;
}

const initialEventingSet = {
  events: null,
  prefetched: null,
  end: false,
};

const initialState: Eventing = {
  trendingMap: {},
  trending: initialEventingSet,
  recent: initialEventingSet,
  user: { ...initialEventingSet, currentUsername: null, currentTag: null },
  tag: { ...initialEventingSet, currentTag: null },
  category: { ...initialEventingSet, currentCategory: null },
  temp: initialEventingSet,
};

const reducer = handleActions(
  {
    [REVEAL_PREFETCHED]: (state, action: RevealPrefetchedAction) => {
      return produce(state, draft => {
        const { payload } = action;
        const { events, prefetched } = draft[payload];
        if (events && prefetched) {
          events.push(...prefetched);
          draft[payload].prefetched = null;
        }
      });
    },
    [CLEAR_USER_EVENTS]: state => {
      return produce(state, draft => {
        draft.user = {
          events: null,
          prefetched: null,
          end: false,
          currentUsername: null,
          currentTag: null,
        };
      });
    },
    [CLEAR_TAG_EVENTS]: state => {
      return produce(state, draft => {
        draft.tag = {
          events: null,
          prefetched: null,
          end: false,
          currentTag: null,
        };
      });
    },
    [CLEAR_CATEGORY_EVENTS]: state => {
      return produce(state, draft => {
        draft.category = {
          events: null,
          prefetched: null,
          end: false,
          currentCategory: null,
        };
      });
    },
    [REMOVE_TEMP_EVENT]: (state, { payload }: RemoveTempEventAction) => {
      return produce(state, draft => {
        if (!draft.temp.events) return;
        draft.temp.events = draft.temp.events.filter(p => p.id !== payload);
      });
    },
    [FLUSH_LIST]: state => {
      return initialState;
    },
  },
  initialState,
);

export default applyPenders(reducer, [
  {
    type: GET_RECENT_EVENTS,
    onSuccess: (state: Eventing, action: EventsResponseAction) => {
      const { category, sort } = action.payload;
      return produce(state, draft => {
        draft.recent = {
          category,
          sort,
          end: false,
          events: action.payload.data,
          prefetched: null,
        };
      });
    },
  },
  {
    type: PREFETCH_RECENT_EVENTS,
    onSuccess: (state: Eventing, action: EventsResponseAction) => {
      const { data } = action.payload;
      return produce(state, draft => {
        draft.recent.prefetched = data;
        if (data && data.length === 0) {
          draft.recent.end = true;
        }
      });
    },
  },
  {
    type: GET_TAG_EVENTS,
    onSuccess: (state: Eventing, action: EventsResponseAction) => {
      return produce(state, draft => {
        draft.tag = {
          end: action.payload.data.length < 20,
          events: action.payload.data,
          prefetched: null,
          currentTag: action.meta.tag,
        };
      });
    },
  },
  {
    type: PREFETCH_TAG_EVENTS,
    onSuccess: (state: Eventing, action: EventsResponseAction) => {
      const { data } = action.payload;
      return produce(state, draft => {
        draft.tag.prefetched = data;
        if (data && data.length === 0) {
          draft.tag.end = true;
        }
      });
    },
  },
  {
    type: GET_CATEGORY_EVENTS,
    onSuccess: (state: Eventing, action: EventsResponseAction) => {
      return produce(state, draft => {
        draft.category = {
          end: action.payload.data.length < 20,
          events: action.payload.data,
          prefetched: null,
          currentCategory: action.meta.category,
          sort: action.meta.sort,
        };
      });
    },
  },
  {
    type: PREFETCH_CATEGORY_EVENTS,
    onSuccess: (state: Eventing, action: EventsResponseAction) => {
      const { data } = action.payload;
      return produce(state, draft => {
        draft.category.prefetched = data;
        if (data && data.length === 0) {
          draft.category.end = true;
        }
      });
    },
  },
  {
    type: GET_TRENDING_EVENTS,
    onSuccess: (state: Eventing, action: EventsResponseAction) => {
      return produce(state, draft => {
        action.payload.data.forEach(event => {
          draft.trendingMap[event.id] = true;
        });
        draft.trending = {
          end: false,
          events: action.payload.data,
          prefetched: null,
        };
      });
    },
  },
  {
    type: PREFETCH_TRENDING_EVENTS,
    onSuccess: (state: Eventing, action: EventsResponseAction) => {
      const { data } = action.payload;
      return produce(state, draft => {
        const filtered = data.filter(event => !state.trendingMap[event.id]);
        draft.trending.prefetched = filtered;
        filtered.forEach(event => {
          draft.trendingMap[event.id] = true;
        });
        if (data && data.length === 0) {
          draft.trending.end = true;
        }
      });
    },
  },
  {
    type: GET_USER_EVENTS,
    onPending: (state: Eventing) => {
      return produce(state, draft => {
        draft.user.end = false;
        draft.user.prefetched = null;
      });
    },
    onSuccess: (state: Eventing, action: EventsResponseAction) => {
      return produce(state, draft => {
        draft.user.events = action.payload.data;
        draft.user.currentUsername = action.meta.username;
        if (action.meta.tag) {
          draft.user.currentTag = action.meta.tag;
        }
        if (action.payload.data.length < 20) {
          draft.user.end = true;
        }
      });
    },
  },
  {
    type: PREFETCH_USER_EVENTS,
    onSuccess: (state: Eventing, action: EventsResponseAction) => {
      const { data } = action.payload;
      return produce(state, draft => {
        draft.user.prefetched = data;
        if (data && data.length === 0) {
          draft.user.end = true;
        }
      });
    },
  },
  {
    type: GET_TEMP_EVENTS,
    onPending: (state: Eventing) => {
      return produce(state, draft => {
        draft.temp.end = false;
        draft.temp.prefetched = null;
      });
    },
    onSuccess: (state: Eventing, action: EventsResponseAction) => {
      return produce(state, draft => {
        draft.temp.events = action.payload.data;
        if (action.payload.data.length < 20) {
          draft.temp.end = true;
        }
      });
    },
  },
  {
    type: PREFETCH_TEMP_EVENTS,
    onSuccess: (state: Eventing, action: EventsResponseAction) => {
      const { data } = action.payload;
      return produce(state, draft => {
        draft.temp.prefetched = data;
        if (data && data.length === 0) {
          draft.temp.end = true;
        }
      });
    },
  },
]);



// WEBPACK FOOTER //
// ./src/store/modules/eventing.js